import React, { useMemo, useState } from 'react';
import { Select } from 'antd';
import { useGetSelectFieldValues } from '@/shared/api/client/field-configuration-controller';
import type { SelectFieldOption } from '@/shared/api/model';

export interface RegionSelectProps {
  disabled?: boolean;
  placeholder?: string;
  value?: string;
  onChange?: (value: string | undefined) => void;
  onBlur?: () => void;
}

/**
 * Region select component.
 * Fetches regions from the 'region' provider.
 *
 * Used for parameter type: REGION_SELECT
 */
const RegionSelect: React.FC<RegionSelectProps> = ({
  disabled,
  placeholder,
  ...rest
}) => {
  const [search, setSearch] = useState<string | undefined>(undefined);

  const params = useMemo(() => ({
    search,
    fieldName: 'region',
    entityType: 'REPORT',
  }), [search]);

  const { data: options, isLoading, error } = useGetSelectFieldValues('region', params as any, {
    query: {
      staleTime: 5 * 60 * 1000,
    },
  });

  const selectOptions = useMemo(() => {
    const list: SelectFieldOption[] = Array.isArray(options) ? options : [];
    return list.map((opt) => ({
      value: opt.value,
      label: opt.label,
      disabled: opt.disabled,
    }));
  }, [options]);

  return (
    <Select
      showSearch
      allowClear
      disabled={disabled}
      loading={isLoading}
      placeholder={placeholder || 'Select region'}
      options={selectOptions}
      filterOption={false}
      onSearch={(q) => setSearch(q)}
      notFoundContent={error ? 'Failed to load regions' : isLoading ? 'Loading...' : 'No regions'}
      optionFilterProp="label"
      {...rest}
    />
  );
};

export default RegionSelect;
